import {
	SendsPageItemType,
	SendsPagesUpdateType,
	updateSendsPage,
	updateSendsPageActiveStatus,
	useChangeSendsStatus,
	useSendsPage,
	useUpdateSendsData,
} from '@ukpostbox/client-data';
import {
	PageHeader,
	Button,
	ContentBlock,
	ContentLoader,
	DropdownMenu,
	ContentSidebarLayout,
	ToggleSwitch,
	ToggleType,
	useLayout,
	useNotifications,
	DropdownMenuEntry,
	Colour,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { ParseURL } from '@ukpostbox/helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { faClipboard } from '@fortawesome/pro-solid-svg-icons/faClipboard';
import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons/faEllipsisVertical';
import { faExternalLink } from '@fortawesome/pro-solid-svg-icons/faExternalLink';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import { faRectangleHistory } from '@fortawesome/pro-solid-svg-icons/faRectangleHistory';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { faWavePulse } from '@fortawesome/pro-solid-svg-icons/faWavePulse';
import PageHistoryModal from '../Components/Modals/PageHistoryModal';
import SendMailHistoryProvider from '../../Providers/SendMailHistoryProvider';
import ConfirmDeleteSendsPageModal from '../Components/Modals/ConfirmDeleteSendsPageModal';
import { useSite } from '../../../_Global/Providers/SiteInterfaceProvider';
import SendsNavigation from '../../../SendMail/Components/Shared/Controls/SendsNavigation';
import CreateStandaloneSendForm from '../Components/CreateSendsForm';
import { EditSendsPageActions } from '../Components/EditSendsPageActions';

function EditSendsPage() {
	const { t } = useTranslation();
	const { isTiny, isSmall, isMedium } = useLayout();
	const { currentLocale, updatePageMeta } = useSite();

	const navigate = useNavigate();

	const { pageId } = useParams();
	const { AddNotification } = useNotifications();
	const { data, status, error }: any = useSendsPage(parseInt(pageId as string, 10));

	const [activeSendsPage, setActiveSendsPage] = useState<any>({ isOpen: false, pageData: null });

	const [pageHistoryOpen, setPageHistoryOpen] = useState<boolean>(false);

	const [savingData, setSavingData] = useState<boolean>(false);
	const [saveError, setSaveError] = useState<any>();

	const updateSendsStatus = useChangeSendsStatus();
	const sendsTemplates = useUpdateSendsData();

	const sendsEditRef = useRef<any>(null);
	const sendMailNavigationRef = useRef<any>(null);

	const HandleSave = async (newData: SendsPageItemType) => {
		setSavingData(true);

		await updateSendsPage(parseInt(pageId as string, 10), newData)
			.then(() => {
				sendsTemplates.mutate({
					updateType: SendsPagesUpdateType.UPDATE,
					newData: { ...data, ...{ ...newData, isActive: data.isActive } },
				});

				AddNotification({
					title: t('results.sendsUpdated'),
					icon: faPencil,
					colour: Colour.Success,
				});
			})
			.catch((e: any) => {
				setSaveError(e.response.data);
			})
			.finally(() => setSavingData(false));
	};

	const HandleTemplateStatus = async (sendsPageId: number, active: any) => {
		await updateSendsPageActiveStatus(sendsPageId, active).then(() => {
			updateSendsStatus.mutate({
				newData: active,
				id: sendsPageId,
			});
		});
	};

	const ShowDeleteConfirmation = (page: SendsPageItemType) => {
		setActiveSendsPage({ pageData: page, isOpen: true });
	};

	const HandleOpen = async (slug: string) => {
		window.open(ParseURL(slug), '_blank');
	};

	const HandleCopyUrl = async (slug: string) => {
		AddNotification({
			title: t('results.copiedClipboard'),
			icon: faClipboard,
		});
		await navigator.clipboard.writeText(ParseURL(slug));
	};

	useEffect(() => {
		updatePageMeta({
			title: t('status.editing', { item: data?.title }),
		});
	}, [currentLocale]);

	return (
		<SendMailHistoryProvider pageId={parseInt(pageId as string, 10)}>
			<ContentSidebarLayout
				ref={sendsEditRef}
				navigationComponent={
					<SendsNavigation
						title={t('status.editing', { item: data?.title })}
						ref={sendMailNavigationRef}
						addonContent={
							<>
								{isMedium && (
									<EditSendsPageActions
										isActive={activeSendsPage?.isActive}
										handleCopyUrlCallback={() => HandleCopyUrl(data.liveUrl ? data.liveUrl : '')}
										handleShowHistoryCallback={() => setPageHistoryOpen(true)}
										handleOpenCallback={() => HandleOpen(data.liveUrl ? data.liveUrl : '')}
										handleShowDeleteConfirmationCallback={() => ShowDeleteConfirmation(data)}
										handleTemplateStatusCallback={() =>
											HandleTemplateStatus(data.id ? data.id : -1, !data?.isActive)
										}
									/>
								)}

								{(isTiny || isSmall) && (
									<DropdownMenu
										title='Sends Dropdown'
										trigger={
											<Button
												squared
												title={t('actions.title')}
												extraClass='edit-page-menu'
												icon={faEllipsisVertical}
												iconOnly
												showArrow
											/>
										}
									>
										<DropdownMenuEntry
											title={t('actions.history')}
											onClick={() => setPageHistoryOpen(true)}
											icon={faRectangleHistory}
										/>

										<DropdownMenuEntry title={t('divider')} sectionDivider />

										<DropdownMenuEntry
											title={t('actions.open')}
											onClick={() => HandleOpen(data.liveUrl ? data.liveUrl : '')}
											icon={faExternalLink}
										/>

										<DropdownMenuEntry
											title={t('actions.copyUrl')}
											onClick={() => HandleCopyUrl(data.liveUrl ? data.liveUrl : '')}
											icon={faCopy}
										/>

										<DropdownMenuEntry title={t('divider')} sectionDivider />

										<DropdownMenuEntry
											title={t('status.active')}
											onClick={() =>
												HandleTemplateStatus(data.id ? data.id : -1, !data?.isActive)
											}
											icon={faWavePulse}
											addonContentEnd={
												<ToggleSwitch
													hideLabel
													type={ToggleType.Switch}
													label={
														data?.isActive ? t('actions.disable') : t('actions.makeActive')
													}
													name='pageActive'
													defaultChecked={data?.isActive}
												/>
											}
										/>

										<DropdownMenuEntry
											title={t('actions.delete')}
											onClick={() => ShowDeleteConfirmation(data)}
											icon={faTrash}
										/>
									</DropdownMenu>
								)}
							</>
						}
					/>
				}
				extraContentClass={`flex-column `}
			>
				{!isTiny && !isSmall && !isMedium && (
					<PageHeader title={t('status.editing', { item: data?.title })} extraClass='section-page-header'>
						<EditSendsPageActions
							isActive={activeSendsPage?.isActive}
							handleCopyUrlCallback={() => HandleCopyUrl(data.liveUrl ? data.liveUrl : '')}
							handleShowHistoryCallback={() => setPageHistoryOpen(true)}
							handleOpenCallback={() => HandleOpen(data.liveUrl ? data.liveUrl : '')}
							handleShowDeleteConfirmationCallback={() => ShowDeleteConfirmation(data)}
							handleTemplateStatusCallback={() =>
								HandleTemplateStatus(data.id ? data.id : -1, !data?.isActive)
							}
						/>
					</PageHeader>
				)}

				<div className='pt-xl pb-xl p-md'>
					{status === 'pending' ? (
						<ContentLoader />
					) : status === 'error' ? (
						<ContentBlock error title={error.name} content={error.message} />
					) : (
						<div className='row mb-xxxl'>
							<div className='col-xs-12 col-xl-6 col-xxl-5 '>
								<CreateStandaloneSendForm
									saveCallback={HandleSave}
									existingData={data}
									savingData={savingData}
									error={saveError}
								/>
							</div>
						</div>
					)}

					<PageHistoryModal
						pageId={parseInt(pageId as string, 10)}
						isOpen={pageHistoryOpen}
						closeCallback={() => setPageHistoryOpen(false)}
					/>
				</div>

				<ConfirmDeleteSendsPageModal
					pageData={data}
					isOpen={activeSendsPage.isOpen}
					closeCallback={() => setActiveSendsPage({ ...activeSendsPage, isOpen: false })}
					actionedCallback={() => navigate('/')}
				/>
			</ContentSidebarLayout>
		</SendMailHistoryProvider>
	);
}

export default EditSendsPage;
